<!-- DatabaseConfigSection.vue -->
<template>
  <div 
    v-if="isStaff && configType === 'collectorconfig' && spaceType === 'workspace' && !isGeneralFileUpload"
    class="space-y-6 glass-panel dark:text-bg-gray"
  >
    <h2 class="font-semibold mb-4 text-gray-800 dark:text-gray-200 capitalize">
      Database Connection
    </h2>

    <!-- Output: Local ships to the Alloy on the agent; InfluxDB details are kept while Local is picked -->
    <div id="collector-output-group">
      <div class="mb-1 flex items-center gap-1.5">
        <label class="font-medium text-gray-700 dark:text-gray-50">Output<span class="pl-1 text-red-500">*</span></label>
        <FieldHelp text="Where this collector sends its metrics. Switching keeps the InfluxDB details for switching back." />
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="option in OUTPUT_OPTIONS"
          :key="option.key"
          :id="'output-' + option.key"
          type="button"
          @click="selectOutput(option.influx)"
          class="px-4 py-2 rounded-md text-sm font-semibold tracking-wide"
          :class="outputClass(option.influx)"
        >{{ option.label }}</button>
      </div>
      <p v-if="!modelValue.enabled" class="mt-2 text-xs text-gray-500 dark:text-gray-400">
        Only for agents installed with Alloy.
      </p>
    </div>

    <div v-if="modelValue.enabled" class="flex grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Database URLs Field -->
      <div class="flex flex-col">
        <label for="urls" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Database URLs <span class="text-red-500">*</span></span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              List of database connection URLs (e.g., http://localhost:8086)
            </div>
          </div>
        </label>
        <div class="space-y-2">
          <div class="flex items-center space-x-2">
            <input
              v-model="urlInput"
              id="url"
              type="url"
              placeholder="https://database.example.com:8086"
              @keyup.enter="addURL"
              :class="[
                'flex-1 border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
                errors?.urls
                  ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
              ]"
            />
            <button
              type="button"
              id="add_url"
              @click="addURL"
              class="btn bg-green-600 text-white hover:bg-green-700"
              :class="{ 'animate-quick-shake animate-glow': urlInput?.length > 10 }"
            >
              Add
            </button>
          </div>
          <ul class="flex flex-wrap gap-2 mt-2">
            <li 
              v-for="(url, index) in modelValue.urls" 
              :key="index"
              id="added_address"
              class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
            > 
              <span class="text-sm font-medium text-gray-700 dark:text-gray-200">
                {{ url }} 
              </span>
              <button
                @click="removeURL(url)"
                type="button"
                class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </li>
          </ul>
        </div>
        <FieldError :message="errors?.urls"/>
      </div>

      <!-- Database Name Field -->
      <div class="flex flex-col">
        <label for="database" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Database Name <span class="text-red-500">*</span></span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              The name of the database to connect to
            </div>
          </div>
        </label>
        <input
          v-model="modelValue.database"
          id="database"
          type="text"
          placeholder="telegraf"
          @focus="emit('clear-error', 'database')"
          :class="[
            'border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2',
            errors?.database ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
          ]"
        />
        <FieldError :message="errors?.database"/>
      </div>

      <!-- Username Field -->
      <div class="flex flex-col">
        <label for="username" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Username</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Database authentication username
            </div>
          </div>
        </label>
        <input
          v-model="modelValue.username"
          id="username"
          type="text"
          placeholder="admin"
          :class="[
            'border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2',
            errors?.username ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
          ]"
        />
        <FieldError :message="errors?.username"/>
      </div>

      <!-- Password Field -->
      <div class="flex flex-col">
        <label for="password" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Password</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Database authentication password
            </div>
          </div>
        </label>
        <div class="relative">
          <input
            v-model="modelValue.password"
            id="password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="••••••••"
            :class="[
              'w-full border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2',
              errors?.password ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
            ]"
          />
          <button
            @click="showPassword = !showPassword"
            type="button"
            class="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-white"
          >
            <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400 hover:text-gray-600"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400 hover:text-gray-600"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="m10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/></svg>
          </button>
        </div>
        <FieldError :message="errors?.password"/>
      </div>

      <!-- Insecure Skip Verify Field -->
      <div class="flex flex-col">
        <label for="insecure_skip_verify" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Skip SSL Verification</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Skip SSL certificate verification (not recommended for production)
            </div>
          </div>
        </label>
        <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
          <input 
            v-model="modelValue.insecure_skip_verify"
            id="insecure_skip_verify"
            type="checkbox"
            class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
          />
          <label 
            for="insecure_skip_verify" 
            class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
          ></label>
        </div>
        <FieldError :message="errors?.insecure_skip_verify"/>
      </div>

      <!-- Timeout Field -->
      <div class="flex flex-col">
        <label for="timeout" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
          <div class="relative group flex space-x-3">
            <span>Connection Timeout(s)</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
              Connection timeout in seconds
            </div>
          </div>
        </label>
        <input
          v-model.number="modelValue.timeout"
          id="timeout"
          min="1"
          type="number"
          placeholder="30"
          :class="[
            'border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2',
            errors?.timeout ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
          ]"
        />
        <FieldError :message="errors?.timeout"/>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import FieldError from '../FieldError.vue' // Assuming you already have this
import FieldHelp from '../FieldHelp.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  isStaff: {
    type: Boolean,
    required: true
  },
  configType: {
    type: String,
    required: true
  },
  spaceType: {
    type: String,
    default: "serverspace"
  },
  isGeneralFileUpload: {
    type: Boolean,
    default: false
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue', 'clear-error'])

const urlInput = ref('')
const showPassword = ref(false)

const OUTPUT_OPTIONS = [
  { key: 'local', label: 'Local (Alloy)', influx: false },
  { key: 'influxdb', label: 'InfluxDB', influx: true },
]

function selectOutput(influx) {
  emit('update:modelValue', { ...props.modelValue, enabled: influx })
  emit('clear-error', 'urls')
  emit('clear-error', 'database')
}

function outputClass(influx) {
  return Boolean(props.modelValue.enabled) === influx
    ? 'bg-blue-600 text-white border border-blue-600'
    : 'bg-transparent border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-500'
}

function addURL() {
  if (!urlInput.value?.trim()) return

  emit('update:modelValue', {
    ...props.modelValue,
    urls: [...props.modelValue.urls, urlInput.value.trim()]
  })

  urlInput.value = ''
  emit('clear-error', 'urls')
}

function removeURL(urlToRemove) {
  emit('update:modelValue', {
    ...props.modelValue,
    urls: props.modelValue.urls.filter(url => url !== urlToRemove)
  })
}
</script>